import React from 'react';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { Uniform } from '../../../../components/uniform/uniform';
import { UniformStep } from '../../../../components/uniform/uniform-step';
import { UniformStepContainer } from '../../../../components/uniform/uniform-step-container';
import { Accordion, AccordionDetails, AccordionSummary } from '../../../../components/accordion';
import { Cell } from '../../../../components/cell';
import { DatePicker } from '../../../../components/date-picker/date-picker';
import { Row } from '../../../../components/row';
import { SingleSelect } from '../../../../components/single-select';
import { CitizenDto, CourseListDto, CreateCourseEnrollmentDto, ReportType } from '../../../../api-service/clients';
import { BulkCourseEnrollmentTitleZone } from './bulk-course-enrollment-titlezone';
import { formatDate, win } from '../../../../utilities';
import { Checkbox } from '../../../../components/checkbox';
import { Box } from '../../../../components/box';
import { FormControlLabel } from '../../../../components/form-control-label';
import { FormControl } from '../../../../components/form-control';
import { YesNo, YesNoComponent } from '../../../../components/radio-group/yes-no-component';
import { Typography } from '../../../../components/typography';
import { CreateCourseEnrollmentDtoErrors } from './bulk-course-enrollment';
import { FormDialog } from '../../../../components/form-dialog';
import { Tooltip } from '../../../../components/tooltip';

interface IBulkCourseEnrollmentComponentProps {
  courses: CourseListDto[];
  selectedCourse: CourseListDto | undefined;
  onCourseChange: (value: CourseListDto | undefined) => void;
  enrollmentRequest: CreateCourseEnrollmentDto;
  onEnrollmentChange: <K extends keyof CreateCourseEnrollmentDto>(name: K, value: CreateCourseEnrollmentDto[K]) => void;
  onValidate: () => boolean;
  onOk: () => Promise<void>;
  isSubmitting: boolean;
  errors: CreateCourseEnrollmentDtoErrors;
  isLoadingCourses: boolean;
  isCitizenLoading: boolean;
  citizens: CitizenDto[];
  errorCitizens: CitizenDto[];
  onErrorDialogClose: () => void;
}
export function BulkCourseEnrollmentComponent(props: IBulkCourseEnrollmentComponentProps) {
  const {
    courses,
    selectedCourse,
    onCourseChange,
    enrollmentRequest: {
      beginAt,
      endAt,
      createNegativeEventOnMissingAttendance,
      createFollowUpAdvisOnMissingAttendance,
      createAdvisIfNotOnTime,
      followDfdgActivityPeriod,
      hasDutyToWork,
      desiredReportType,
    },
    onEnrollmentChange,
    onValidate,
    onOk,
    isSubmitting,
    errors,
    isLoadingCourses,
    isCitizenLoading,
    citizens,
    errorCitizens,
    onErrorDialogClose,
  } = props;

  return (
    <>
      <Uniform onOk={onOk} useLoaderButtonAsOk okButtonIsLoading={isSubmitting} onCancel={() => win.close()} titleZone={<BulkCourseEnrollmentTitleZone />} showButtons>
        <UniformStep stepName="Oplysninger" validate={onValidate}>
          <UniformStepContainer>
            <Accordion defaultExpanded>
              <AccordionSummary>Hold og periode</AccordionSummary>
              <AccordionDetails>
                <Box mt={1}>
                  <Row>
                    <Cell size="half">
                      <SingleSelect<CourseListDto>
                        label="Hold"
                        onChange={onCourseChange}
                        options={courses}
                        value={selectedCourse}
                        isOptionEqualToValue={(option, val) => option.courseId === val.courseId}
                        getOptionLabel={x => x.name ?? ''}
                        renderOption={(optionProps: object, option: CourseListDto) => (
                          <li {...optionProps} key={option.courseId}>
                            {option.name}
                          </li>
                        )}
                        variant="outlined"
                        required
                        error={!!errors.courseId}
                        errorText={errors.courseId}
                        loading={isLoadingCourses}
                      />
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      <FormControl sx={{ marginLeft: '3px' }}>
                        <FormControlLabel
                          control={<Checkbox checked={followDfdgActivityPeriod ?? false} onChange={(_e, checked) => onEnrollmentChange('followDfdgActivityPeriod', checked)} />}
                          label="Følg aktivitetens periode"
                        />
                      </FormControl>
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="half">
                      <DatePicker
                        fullWidth
                        label="Startdato"
                        name="startDate"
                        value={beginAt}
                        onChange={value => onEnrollmentChange('beginAt', value ?? '')}
                        variant="outlined"
                        required={!followDfdgActivityPeriod}
                        error={!!errors.beginAt}
                        errorText={errors.beginAt}
                        disabled={followDfdgActivityPeriod}
                      />
                    </Cell>
                    <Cell size="half">
                      <DatePicker
                        fullWidth
                        label="Slutdato"
                        name="endDate"
                        value={endAt}
                        onChange={value => onEnrollmentChange('endAt', value ?? '')}
                        variant="outlined"
                        required={!followDfdgActivityPeriod}
                        error={!!errors.endAt}
                        errorText={errors.endAt}
                        disabled={followDfdgActivityPeriod}
                      />
                    </Cell>
                  </Row>
                </Box>
              </AccordionDetails>
            </Accordion>
            <Accordion defaultExpanded>
              <AccordionSummary>Indstillinger for fremmødeforløb</AccordionSummary>
              <AccordionDetails>
                <Box mt={1}>
                  <Row>
                    <Cell size="full">
                      <Typography variant="subtitle2">Arbejdspligt</Typography>
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      <YesNoComponent
                        header="Indgår i borgers arbejdspligt"
                        name="hasDutyToWork"
                        value={hasDutyToWork ? YesNo.Yes : YesNo.No}
                        onChange={value => onEnrollmentChange('hasDutyToWork', value === YesNo.Yes)}
                        hideNotEvaluated
                      />
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      <Box mt={2}>
                        <Typography variant="subtitle2">Hændelser og adviser</Typography>
                      </Box>
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      <YesNoComponent
                        header="Opret negativ hændelse ved manglende fremmøde"
                        name="createNegativeEventOnMissingAttendance"
                        value={createNegativeEventOnMissingAttendance ? YesNo.Yes : YesNo.No}
                        onChange={value => onEnrollmentChange('createNegativeEventOnMissingAttendance', value === YesNo.Yes)}
                        hideNotEvaluated
                      />
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      <YesNoComponent
                        header="Opret advis ved manglende fremmøde"
                        name="createFollowUpAdvisOnMissingAttendance"
                        value={createFollowUpAdvisOnMissingAttendance ? YesNo.Yes : YesNo.No}
                        onChange={value => onEnrollmentChange('createFollowUpAdvisOnMissingAttendance', value === YesNo.Yes)}
                        hideNotEvaluated
                      />
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      <YesNoComponent
                        header="Opret advis ved for sent fremmøde"
                        name="createAdvisIfNotOnTime"
                        value={createAdvisIfNotOnTime ? YesNo.Yes : YesNo.No}
                        onChange={value => onEnrollmentChange('createAdvisIfNotOnTime', value === YesNo.Yes)}
                        hideNotEvaluated
                      />
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="full">
                      <Box mt={2}>
                        <Typography variant="subtitle2">Journalisering</Typography>
                      </Box>
                    </Cell>
                  </Row>
                  <Row>
                    <Cell size="half">
                      <SingleSelect<ReportType>
                        label="Frekvens for fremmøderapport"
                        value={desiredReportType}
                        options={[ReportType.Weekly, ReportType.Monthly]}
                        getOptionLabel={x => getReportDisplay(x)}
                        renderOption={(optionProps: object, option: ReportType) => (
                          <li {...optionProps} key={option}>
                            {getReportDisplay(option)}
                          </li>
                        )}
                        onChange={value => onEnrollmentChange('desiredReportType', value)}
                        variant="outlined"
                      />
                    </Cell>
                  </Row>
                </Box>
              </AccordionDetails>
            </Accordion>
            <Accordion defaultExpanded>
              <AccordionSummary>Borgere</AccordionSummary>
              <AccordionDetails>
                <DataGridPro
                  rows={citizens}
                  columns={citizenColumns(false)}
                  getRowId={row => row.citizenId}
                  autoHeight
                  density="compact"
                  disableColumnMenu
                  disableSelectionOnClick
                  hideFooter
                  loading={isCitizenLoading}
                />
              </AccordionDetails>
            </Accordion>
          </UniformStepContainer>
        </UniformStep>
      </Uniform>
      <FormDialog open={errorCitizens.length > 0} title="Fejl ved oprettelse af fremmødeforløb" confirmButtonText="Ok" onConfirmClick={onErrorDialogClose} maxWidth="lg" fullWidth>
        <Box mb={2}>En eller flere borgere kunne ikke oprettes på det valgte hold.</Box>
        <DataGridPro
          rows={errorCitizens}
          columns={citizenColumns(true)}
          getRowId={row => row.citizenId}
          autoHeight
          density="compact"
          disableColumnMenu
          disableSelectionOnClick
          hideFooter
        />
      </FormDialog>
    </>
  );
}
function citizenColumns(showErrorColumn: boolean) {
  const columns = [
    { field: 'cpr', headerName: 'CPR', flex: 1 },
    { field: 'fullName', headerName: 'Navn', flex: 2 },
    { field: 'activityName', headerName: 'Aktivitet', flex: 2 },
    { field: 'startDate', headerName: 'Aktivitet Startdato', flex: 2, valueGetter: params => (params.value ? formatDate(params.value) : '') },
    { field: 'endDate', headerName: 'Aktivitet Slutdato', flex: 2, valueGetter: params => (params.value ? formatDate(params.value) : '') },
  ] as GridColDef[];
  if (showErrorColumn) {
    columns.push({
      field: 'error',
      headerName: 'Årsag',
      flex: 2,

      renderCell: params => (
        <Tooltip title={translate(params.value)}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{translate(params.value)}</span>
        </Tooltip>
      ),
    });
  }
  return columns;
}
function translate(text: string) {
  if (text === "There's already an subjectactivity for the same activity in the requested period") {
    return 'Der er allerede en et fremmødeforløb for den samme aktivitet i den ønskede periode';
  }
  return text;
}
function getReportDisplay(value: ReportType): string {
  switch (value) {
    case ReportType.Never:
      return 'Aldrig';
    case ReportType.Weekly:
      return 'Ugenlig';
    case ReportType.Monthly:
      return 'Månedlig';
    default:
      return 'Aldrig';
  }
}
